import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { matches, pull } from '@sama/micro-dash';
import { defer, from, lastValueFrom } from 'rxjs';
import { TokenResource } from '../../shared/resources/token-resource.service';
import { TokenScope } from '../../team-apis/otterdam/token-typings';

const EAGER_TOKEN_REFRESH_WINDOW = 5 * 60 * 1000; // 5 minutes

interface CacheItem {
  scope: string;
  tokenPromise: Promise<{ token: string; expiresAt: number }>;
}

@Injectable({ providedIn: 'root' })
export class TokenManagementService {
  private cache: CacheItem[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly tokenResource: TokenResource,
  ) {}

  /**
   * Get a token for the given scope. If forceRefresh is true, a new token will
   * be fetched, even if a cached token exists.
   */
  async getToken(scope: TokenScope, forceRefresh = false) {
    if (scope === 'access_token') {
      // Auth0 AuthService has its own cache
      return this.getAccessToken(forceRefresh);
    }

    return this.getOtherToken(scope, forceRefresh);
  }

  async getAccessToken(forceRefresh = false) {
    return lastValueFrom(
      this.authService.getAccessTokenSilently({
        cacheMode: forceRefresh ? 'off' : 'on',
      }),
    );
  }

  async getOtherToken(scope: TokenScope, forceRefresh = false) {
    await this.removeFromCacheIfExpired(scope);

    let cacheItem = this.cache.find(matches({ scope }));

    if (forceRefresh && cacheItem) {
      pull(this.cache, cacheItem);
      cacheItem = undefined;
    }

    if (!cacheItem) {
      cacheItem = {
        scope,
        tokenPromise: this.getAccessToken().then((accessToken) =>
          this.sendRequest(scope, accessToken),
        ),
      };
      this.cache.push(cacheItem);
    }
    return cacheItem.tokenPromise.then((tokenInfo) => tokenInfo.token);
  }

  getToken$(scope: TokenScope, forceRefresh = false) {
    // defer factory is executed on each subscription, particularly useful for
    // rxjs retry
    return defer(() => from(this.getToken(scope, forceRefresh)));
  }

  private async removeFromCacheIfExpired(scope: TokenScope) {
    const cacheItem = this.cache.find(matches({ scope }));
    if (!cacheItem) {
      return;
    }

    const cachedTokenInfo = await cacheItem.tokenPromise;
    // Warning: possible race condition
    // By the time this starts executing again, a concurrent call to getToken()
    // might have already replaced cacheItem with a new promise.
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (cachedTokenInfo && cachedTokenInfo.expiresAt < Date.now()) {
      // Remove only the cacheItem that we know is expired, if still present
      pull(this.cache, cacheItem);
    }
  }

  private async sendRequest(scope: TokenScope, accessToken: string) {
    return this.tokenResource.getToken(scope, accessToken).then((response) => ({
      scope,
      token: response.access_token,
      expiresAt:
        Date.now() + response.expires_in * 1000 - EAGER_TOKEN_REFRESH_WINDOW,
    }));
  }
}
